import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import { useState } from 'react'

import { SubmissionForm } from '@/app/SubmissionForm'
import { ThemeToggle } from '@/components'
import { useIntakeAuth } from '@/hooks/useIntakeAuth'
import type { CreateUploadResponse } from '@/services/definitions'
import type { ColorMode } from '@/utils/colorMode'

export interface AppProps {
  mode: ColorMode
  onToggleMode: () => void
}

export function App({ mode, onToggleMode }: AppProps): React.JSX.Element {
  const {
    bootstrapError,
    config,
    getIdToken,
    isSigningIn,
    signIn,
    signInError,
    signOutUser,
    status,
    user
  } = useIntakeAuth()
  const [submitted, setSubmitted] = useState<CreateUploadResponse | null>(null)

  return (
    <Container maxWidth='sm' sx={{ py: 5 }}>
      <Stack direction='row' alignItems='flex-start' spacing={2} mb={3}>
        <Stack spacing={1} flexGrow={1}>
          <Typography variant='overline' color='primary' lineHeight={1}>
            Launchpad
          </Typography>
          <Typography variant='h5' component='h1'>
            Launch your idea to a live app
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Upload a single self-contained .html app or .md skill file. It goes through automated and
            human review before it is published.
          </Typography>
        </Stack>
        <ThemeToggle mode={mode} onToggle={onToggleMode} />
      </Stack>

      {status === 'loading' && (
        <Box display='flex' justifyContent='center' py={4}>
          <CircularProgress aria-label='Loading' />
        </Box>
      )}

      {bootstrapError && <Alert severity='error'>{bootstrapError}</Alert>}

      {status === 'ready' && config && !user && (
        <Paper variant='form'>
          <Stack spacing={2} alignItems='flex-start'>
            <Typography variant='h6' component='h2'>
              Sign in
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Submissions are restricted to Barti staff accounts.
            </Typography>
            {signInError && (
              <Alert severity='error'>
                {signInError}
              </Alert>
            )}
            <Button variant='contained' onClick={() => void signIn()} disabled={isSigningIn}>
              {isSigningIn ? 'Signing in…' : 'Sign in with Google'}
            </Button>
          </Stack>
        </Paper>
      )}

      {status === 'ready' && config && user && (
        <Paper variant='form'>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            spacing={2}
            pb={2}
            mb={3}
            borderBottom={1}
            borderColor='divider'
          >
            <Typography variant='body2' color='text.secondary' sx={{ overflowWrap: 'anywhere' }}>
              {user.email}
            </Typography>
            <Link component='button' type='button' variant='body2' onClick={() => void signOutUser()}>
              Sign out
            </Link>
          </Stack>

          {submitted
            ? (
              <Stack spacing={2} alignItems='flex-start'>
                <Alert severity='success'>
                  Your app was uploaded. It now goes through automated and human
                  review; you will hear back in Slack.
                </Alert>
                <Typography variant='caption' color='text.secondary'>
                  Submission ID
                </Typography>
                <Typography variant='body2' fontFamily='monospace' sx={{ overflowWrap: 'anywhere' }}>
                  {submitted.submission_id}
                </Typography>
                <Button variant='outlined' onClick={() => setSubmitted(null)}>
                  Submit another app
                </Button>
              </Stack>
            )
            : (
              <SubmissionForm
                config={config}
                getIdToken={getIdToken}
                onSubmitted={setSubmitted}
              />
            )}
        </Paper>
      )}
    </Container>
  )
}
